import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PhotoIcon } from '@heroicons/react/24/outline';
import BannerCard from '../components/BannerCard';
import BannerModal from '../components/BannerModal';
import ConfirmDialog from '../components/ConfirmDialog';

const MortgageLoanBannersPage = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, bannerId: null });

    const PAGE_TYPE = 'mortgage_loan';

    // Fetch banners
    const fetchBanners = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/banners');
            console.log('All banners response:', response.data);

            // Filter for mortgage_loan page type
            const allBanners = response.data?.data || [];
            const filteredBanners = Array.isArray(allBanners)
                ? allBanners.filter(banner => banner.page_type === PAGE_TYPE)
                : [];

            console.log('Filtered banners:', filteredBanners);
            setBanners(filteredBanners);
        } catch (error) {
            console.error('Error fetching banners:', error);
            toast.error('Failed to load banners');
            setBanners([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingBanner) {
                await axios.put(`/api/admin/banners/${editingBanner.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Banner updated successfully');
            } else {
                await axios.post('/api/admin/banners', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Banner created successfully');
            }
            setIsModalOpen(false);
            setEditingBanner(null);
            fetchBanners();
        } catch (error) {
            console.error('Error saving banner:', error);
            toast.error(error.response?.data?.message || 'Failed to save banner');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, bannerId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/banners/${deleteConfirm.bannerId}`);
            toast.success('Banner deleted successfully');
            setDeleteConfirm({ isOpen: false, bannerId: null });
            fetchBanners();
        } catch (error) {
            console.error('Error deleting banner:', error);
            toast.error('Failed to delete banner');
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Mortgage Loan Banners</h1>
                    <p className="text-slate-500 mt-1">Manage banner content for Mortgage Loan page</p>
                </div>
                <button
                    onClick={() => {
                        setEditingBanner(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Banner
                </button>
            </div>

            {/* Banners Grid */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-6">Banner Items</h2>
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-2xl h-64 shadow-sm animate-pulse border border-slate-100"></div>
                        ))}
                    </div>
                ) : banners.length === 0 ? (
                    <div className="bg-slate-50 rounded-2xl p-12 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <PhotoIcon className="h-8 w-8 text-slate-400" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 mb-2">No banners yet</h3>
                            <p className="text-slate-500 mb-6">Add your first banner to showcase on the Mortgage Loan page.</p>
                            <button
                                onClick={() => {
                                    setEditingBanner(null);
                                    setIsModalOpen(true);
                                }}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                            >
                                Add First Banner
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {banners.map((banner) => (
                            <BannerCard
                                key={banner.id}
                                banner={banner}
                                onEdit={(b) => {
                                    setEditingBanner(b);
                                    setIsModalOpen(true);
                                }}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {/* Modal */}
            {isModalOpen && (
                <BannerModal
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingBanner(null);
                    }}
                    onSave={() => {
                        setIsModalOpen(false);
                        setEditingBanner(null);
                        fetchBanners();
                    }}
                    banner={editingBanner}
                    defaultPageType={PAGE_TYPE}
                />
            )}

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, bannerId: null })}
                onConfirm={confirmDelete}
                title="Delete Banner"
                message="Are you sure you want to delete this banner? This action cannot be undone."
            />
        </div>
    );
};

export default MortgageLoanBannersPage;
